import React, { useMemo } from "react";

import { WorkBreakDownOrder } from "@/constants/constants";
import { RoleEnum } from "@/interfaces/globals";
import { ReportData } from "@/interfaces/interfaces";
import { TimeShift } from "@/interfaces/timeTracking";
import { useGetSingleDaySheetQuery } from "@/redux/api/rosterQuery";
import { useGetShiftsQuery } from "@/redux/api/timeTrackingSettingQuery";
import {
  TDailyReport,
  TEmployeeWorkingHour,
} from "@/schemas/daily-report.schema";
import {
  sortShifts,
  timeToSeconds,
} from "@/utils/helper-functions/date-time.helper";

interface UseDailyReportFormatValuesProps {
  rosterId?: string;
}

interface UseDailyReportFormatValuesReturnType {
  getFormattedValues: (data: ReportData) => TDailyReport;
  rosterEmployeeWH: TEmployeeWorkingHour[];
  daySheetIds: string[];
  projectandCustomerInfo: {
    projectId: string;
    customerId: string;
    date: string | Date;
  };
}

export const useDailyReportFormatValues = (
  props: UseDailyReportFormatValuesProps,
): UseDailyReportFormatValuesReturnType => {
  const { rosterId } = props;
  let daySheetIds: string[] = [];

  const { data: shifts } = useGetShiftsQuery(RoleEnum.EMPLOYEE);

  const { data: singleDaysheetData } = useGetSingleDaySheetQuery(
    { id: rosterId || "" },
    {
      skip: !rosterId,
    },
  );
  const daysheetData = useMemo(() => {
    return singleDaysheetData ? [singleDaysheetData] : [];
  }, [singleDaysheetData]);

  const projectandCustomerInfo = (singleDaysheetData && {
    projectId: singleDaysheetData.projectId || "",
    customerId: singleDaysheetData.customerId || "",
    date: singleDaysheetData.date,
  }) || {
    projectId: "",
    customerId: "",
    date: "",
  };

  daySheetIds = singleDaysheetData ? [singleDaysheetData.id] : [];

  const rosterEmployeeWH: TEmployeeWorkingHour[] = React.useMemo(() => {
    if (!daysheetData || !shifts || shifts.length === 0) {
      return [];
    }

    if (daysheetData.length === 0) {
      return [];
    }

    return daysheetData?.flatMap((dayEntry) => {
      return dayEntry.employees.map((employee) => {
        const clockInTime = employee.clockIn
          ? timeToSeconds(employee.clockIn.toString())
          : 0;
        const clockOutTime = employee.clockOut
          ? timeToSeconds(employee.clockOut.toString())
          : 0;
        const employeeId = employee.employeeId || "";
        const breakDuration = employee.break || 0;
        const totalHours = (clockOutTime - clockInTime) / 3600 || 0;

        const reformat = sortShifts(shifts);
        const [sortedShifts] = reformat;
        const defaultBreakDowns = sortedShifts.map((shift: TimeShift) => ({
          shiftId: shift.id,
          time: 0,
        }));

        return {
          id: "",
          employeeId,
          startTime: clockInTime,
          endTime: clockOutTime,
          break: breakDuration,
          totalHours,
          note: "",
          files: [],
          workBreakDown: defaultBreakDowns,
          location: employee.location,
        };
      });
    });
  }, [daysheetData, shifts]);

  const getFormattedValues = (data: ReportData): TDailyReport => {
    const formattedResourceRecoveryDetails =
      data?.resorceRecoveryDetails.flat();

    return {
      id: data?.id || "",
      date: data?.date || "",
      customerId: data?.customerId || "",
      projectId: data?.projectId || "",
      employeeWorkingHours:
        data?.employeeWorkingHours?.map((employee) => ({
          id: employee?.id || "",
          employeeId: employee?.employeeId || "",
          startTime: employee?.startTime || 0,
          endTime: employee?.endTime || 0,
          break: employee?.breakTime || 0,
          note: employee?.note || "",
          files: employee?.files || [],
          workBreakDown: (
            employee?.employeeWorkHourBreakdown?.map(
              (breakdown: {
                id: string;
                workshift: { id: string; title: string };
                time: number;
              }) => ({
                id: breakdown?.id || "",
                shiftId: breakdown?.workshift?.id || "",
                title: breakdown?.workshift?.title || "",
                time: breakdown?.time || 0,
              }),
            ) || []
          ).sort((a: { title: string }, b: { title: string }) => {
            const order = ["Normal Hours", "Overnight Hours", "Weekend"];

            return order.indexOf(a.title) - order.indexOf(b.title);
          }),
          totalHours: employee?.totalHours || 0,
          location: employee.location || [],
        })) || [],
      subcontractorWorkingHours:
        data?.subcontractorWorkHours?.map((subcontractor) => ({
          id: subcontractor?.id || "",
          subcontractorId: subcontractor?.subcontractorId || "",
          startTime: subcontractor?.startTime || 0,
          endTime: subcontractor?.endTime || 0,
          break: subcontractor?.breakTime || 0,
          note: subcontractor?.note || "",
          files: subcontractor?.files || [],
          workBreakDown: (
            subcontractor?.subContractorWorkHourBreakdown?.map(
              (breakdown: {
                id: string;
                workshift: { id: string; title: string };
                time: number;
              }) => ({
                id: breakdown?.id || "",
                shiftId: breakdown?.workshift.id || "",
                title: breakdown?.workshift?.title || "",
                time: breakdown?.time || 0,
              }),
            ) || []
          ).sort((a: { title: string }, b: { title: string }) => {
            return (
              WorkBreakDownOrder.indexOf(a.title) -
              WorkBreakDownOrder.indexOf(b.title)
            );
          }),
          totalHours: subcontractor?.totalHours || 0,
        })) || [],
      resourceRecoveryDetails: {
        id: formattedResourceRecoveryDetails?.filter(
          (val) => !val.isFromTab,
        )?.[0]?.id, // This ID refers to the custom resource recovery details created by the user, not those generated automatically by the system or selected from a tab
        usedTickets: formattedResourceRecoveryDetails
          ?.filter((details) => details.isFromTab)
          ?.map((formatted) => formatted.id),
        tippingRecords: formattedResourceRecoveryDetails
          ?.filter((details) => details.isFromTab)
          ?.map((formatted) => ({
            label: `Ticket - ${formatted.ticketNumber.toString()}`,
            value: formatted.id,
          })),
        manualData: formattedResourceRecoveryDetails
          ?.map((formatted) =>
            formatted?.weighScaleTicketItems?.map((item) => {
              return {
                id: item?.id || "",
                tippingId: formatted?.isFromTab ? item?.weighScaleTicketId : "",
                wasteType: {
                  id: item?.wasteType?.id || "",
                  weight: item?.wasteTypeWeight || 0,
                },
                loadType: {
                  id: item?.loadType?.id || "",
                  quantity: item?.loadTypeQuantity || 0,
                },
                breakDown: {
                  mixedWaste: item?.breakDownData?.mixedWaste?.map((waste) => ({
                    id: waste?.id || "",
                    wasteTypeId: waste?.wasteTypeId || "",
                    wasteName: waste?.wasteName || "",
                    percentage: +waste?.percentage || 0,
                  })),
                  otherWaste: item?.breakDownData?.otherWaste?.map((waste) => ({
                    id: waste?.id || "",
                    wasteTypeId: waste?.wasteTypeId || "",
                    wasteName: waste?.wasteName || "",
                  })),
                },
              };
            }),
          )
          .flat(),
      },
    };
  };

  return {
    getFormattedValues,
    rosterEmployeeWH,
    daySheetIds,
    projectandCustomerInfo,
  };
};
